import { useState } from "react";
import { Eye, EyeOff, Loader2, Unplug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InputVenture } from "@/components/ui/input-venture";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface FathomKeyFormCloser {
  id: string;
  name: string;
  fathom_api_key_vault_ref: string | null;
}

export interface FathomKeyFormProps {
  closer: FathomKeyFormCloser;
  onSuccess: () => void;
  /**
   * Optional "close" callback. When rendered inside a Dialog, the wrapper
   * passes `() => onOpenChange(false)`. When rendered as a standalone page
   * (/closer/fathom) the caller omits it — the form simply stays mounted
   * after a successful operation.
   */
  onDone?: () => void;
}

/**
 * Shared Fathom key connect/rotate/disconnect form.
 *
 * Phase 16 plan 16-05 (D-13): extracted from `FathomKeyModal` so both the
 * admin modal and the new `/closer/fathom` page can reuse the same form
 * without duplicating state + edge-function plumbing. The form renders no
 * Dialog/DialogContent wrappers — callers decide the surrounding shell.
 */
export function FathomKeyForm({ closer, onSuccess, onDone }: FathomKeyFormProps) {
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const isRotateMode = !!closer.fathom_api_key_vault_ref;

  const handleConnect = async () => {
    if (!apiKey.trim()) return;

    setConnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke("fathom-key-validate", {
        body: { action: "connect", closer_id: closer.id, api_key: apiKey.trim() },
      });

      if (error) {
        toast.error(error.message || "Failed to connect Fathom key");
        return;
      }
      if (data?.error) {
        toast.error(data.error);
        return;
      }

      toast.success(`Fathom connected for ${closer.name}`);
      setApiKey("");
      setShowKey(false);
      onSuccess();
      onDone?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Unexpected error");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke("fathom-key-validate", {
        body: { action: "disconnect", closer_id: closer.id },
      });

      if (error) {
        toast.error(error.message || "Failed to disconnect Fathom");
        return;
      }
      if (data?.error) {
        toast.error(data.error);
        return;
      }

      toast.success(`Fathom disconnected for ${closer.name}`);
      setApiKey("");
      setShowKey(false);
      onSuccess();
      onDone?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Unexpected error");
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="fathom-api-key" className="text-sm font-medium">
          Fathom API Key
        </label>
        <div className="relative">
          <InputVenture
            id="fathom-api-key"
            type={showKey ? "text" : "password"}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="fathom_..."
            className="pr-10"
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setShowKey(!showKey)}
            aria-label={showKey ? "Hide API key" : "Show API key"}
          >
            {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          Find your API key at fathom.video &gt; Settings &gt; API
        </p>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        {isRotateMode && (
          <Button
            variant="danger"
            size="sm"
            onClick={handleDisconnect}
            disabled={disconnecting || connecting}
            className="w-full sm:w-auto"
          >
            {disconnecting ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Unplug className="h-4 w-4 mr-1" />
            )}
            Disconnect
          </Button>
        )}
        <Button
          onClick={handleConnect}
          disabled={!apiKey.trim() || connecting || disconnecting}
          className="w-full sm:w-auto"
        >
          {connecting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
          {isRotateMode ? "Update Key" : "Connect"}
        </Button>
      </div>
    </div>
  );
}
